/**
 * Design Editor (Konva canvas) — Learn-Mode inline tips, part 1.
 *
 * Covers `routes/social/DesignEditor.tsx`, `components/DesignAttachNotice.tsx`,
 * and the first batch of `components/design-editor/*` panels. The canvas
 * itself (`design-editor-canvas`) is intentionally EXCLUDED from the "?"
 * badge overlay at runtime (see `LearnTipsOverlay.tsx`'s
 * `EXCLUDED_CONTEXT_KEYS`) because it's the live Konva Stage container — a
 * portaled badge there would sit on top of canvas pointer interaction. Its
 * tip is still registered here for coverage.
 */

import type { LearnTip } from "./types";

export const designEditorTips: LearnTip[] = [
  {
    key: "design-editor",
    title: "Design Editor",
    body: "The full design workspace — canvas in the center, tool panels on the sides, and a toolbar above that changes based on what's selected.",
  },
  {
    key: "design-editor-canvas",
    title: "Canvas",
    body: "Your design surface. Click an element to select it, drag to move it, and use the toolbar above for the tools that apply to your current selection. Press the question mark key for keyboard shortcuts.",
  },
  {
    key: "design-attach-notice",
    title: "Attached to Post",
    body: "Confirms this design's pages were successfully attached as images to a post. Dismiss it once you've seen it.",
  },
  {
    key: "design-editor-align",
    title: "Align",
    body: "Lines up the selected elements relative to each other, or to the page when nothing else is selected to align against.",
  },
  {
    key: "design-studio-asset-library",
    title: "Asset Library",
    body: "Browse and insert images from your uploads, the client's approved asset library, or stock photos — all without leaving the editor.",
  },
  {
    key: "design-studio-upload",
    title: "Upload",
    body: "Add your own image to this design. Drag and drop a file here, or click to choose one from your device.",
  },
  {
    key: "design-studio-client-library",
    title: "Client Library",
    body: "Pre-approved images from this client's own asset library — click one to insert it directly into the design.",
  },
  {
    key: "design-studio-stock",
    title: "Stock Photos",
    body: "Search royalty-free stock photography and click a result to insert it into your design.",
  },
  {
    key: "design-studio-gifs",
    title: "GIFs & Stickers",
    body: "Search GIPHY (PG-rated) and click a GIF or sticker to add it to the design as a still image — the first frame. For moving GIFs, add them in the video editor instead. Organic posts only — not for ads.",
  },
  {
    key: "design-magic-resize",
    title: "Magic Resize",
    body: "Automatically recreates this design at other canvas sizes (e.g. a square post becomes a Story) by repositioning the existing elements, so you don't have to rebuild it by hand for every platform.",
  },
  {
    key: "design-studio-brand-panel",
    title: "Brand Kit Panel",
    body: "Apply the selected client's brand colors, fonts, and logo to this design, or pull in approved images from their brand library.",
  },
  {
    key: "design-studio-apply-brand",
    title: "Apply Brand to This Design",
    body: "Applies the client's current brand colors, fonts, and logo to every element in this design in one click. Choose whether the background recolors too.",
  },
  {
    key: "design-studio-brand-client-library",
    title: "Client Library",
    body: "Approved images from this client's asset library, available to insert directly from the brand panel.",
  },
  {
    key: "design-editor-context-menu",
    title: "Right-Click Menu",
    body: "Quick actions for the selected element (or the canvas itself if nothing's selected) — duplicate, layer order, lock, delete, and more.",
  },
  {
    key: "design-color-picker",
    title: "Color Picker",
    body: "Pick a color for the selected element — choose from the client's brand palette, recent colors, or enter a custom hex value.",
  },
  {
    key: "design-contextual-toolbar",
    title: "Selection Toolbar",
    body: "Tools that change based on what you've selected — text formatting for a text box, fill and effects for a shape or image, and so on.",
  },
  {
    key: "design-editor-crop",
    title: "Crop",
    body: "Drag the crop window to choose which part of the image shows in your design. The dimmed area outside the window is hidden, not deleted — you can re-crop later.",
  },
  {
    key: "design-studio-data-widget-editor",
    title: "Editing a Data Widget",
    body: "Edit the content behind a chart, table, or QR code element directly here — the widget on the canvas updates live as you type.",
  },
  {
    key: "design-editor-upload-status",
    title: "Upload Status",
    body: "Shows images still saving to your uploads in the background, and flags any that failed so you can retry.",
  },
  {
    key: "design-image-effects",
    title: "Filters",
    body: "Apply a one-click photo filter to the selected image, or fine-tune individual adjustments like brightness and contrast.",
  },
  {
    key: "design-text-effects",
    title: "Text Effects",
    body: "Add a visual effect to the selected text — outline, shadow, glow, or a background — beyond plain formatting.",
  },
  {
    key: "design-studio-elements-panel",
    title: "Elements",
    body: "Insert shapes, lines, frames, grids, emoji, charts, and QR codes onto the canvas.",
  },
  {
    key: "design-studio-elements-search",
    title: "Search Elements",
    body: "Search every element category at once instead of browsing each tab individually.",
  },
  {
    key: "design-studio-lines",
    title: "Lines",
    body: "Insert a divider or decorative line onto the canvas.",
  },
  {
    key: "design-studio-frames",
    title: "Frames",
    body: "Insert a shaped photo frame, then click a photo (in the Asset Library or already selected) to fill it — or click a frame with a photo already selected to crop it into that shape.",
  },
  {
    key: "design-studio-grids",
    title: "Grids",
    body: "Fills the page with a grid of photo frames in one step. Select a cell, then click a photo to fill it. Adjust the spacing between cells before inserting.",
  },
  {
    key: "design-studio-emoji",
    title: "Emoji",
    body: "Insert an emoji onto the canvas as its own element, grouped by category.",
  },
  {
    key: "design-studio-charts",
    title: "Charts & QR Codes",
    body: "Insert a live chart (bar, line, pie) built from data you enter, or a QR code that links to a URL you type in.",
  },
  {
    key: "design-studio-qr",
    title: "QR Code",
    body: "Type the URL or text you want the code to link to, then insert it onto the canvas.",
  },
  {
    key: "design-export",
    title: "Export Design",
    body: "Download this design as an image or video file, or generate a print-ready PDF.",
  },
  {
    key: "design-export-mode",
    title: "Image or Video",
    body: "Choose whether to export this design as a static image or, if it has animation, as a video file.",
  },
  {
    key: "design-export-pages",
    title: "Which Pages",
    body: "Choose whether to export just the page you're currently viewing or every page in the design.",
  },
  {
    key: "design-export-print",
    title: "Print PDF",
    body: "Generates a print-shop-ready PDF with vector text and outlined fonts. Backgrounds extend past the trim line (bleed) and crop marks show where to cut. Save the design first — this always uses your latest edits.",
  },
  {
    key: "design-editor-layers",
    title: "Layers",
    body: "Every element on the current page, stacked in the order they render. Drag to reorder, or click an element here to select it on the canvas.",
  },
  {
    key: "design-pages",
    title: "Pages",
    body: "Multi-page designs show their pages here — click to jump between them, or add notes for a page (never exported, just for your team).",
  },
  {
    key: "design-format-picker",
    title: "Choose a Format",
    body: "Pick a preset canvas size for your new design, or enter custom dimensions.",
  },
  {
    key: "design-element-shadow",
    title: "Drop Shadow",
    body: "Adds a drop shadow behind the selected element. Turn it on, then adjust the color, blur, and offset to taste.",
  },
  {
    key: "design-properties-panel",
    title: "Properties",
    body: "Settings for whatever is currently selected — one element's properties, alignment tools when several are selected, or canvas-wide settings when nothing is.",
  },
  {
    key: "design-replace-image",
    title: "Replace Image",
    body: "Swap out this frame's photo for a different one without rebuilding the design — the frame's size, shape, and position stay the same.",
  },
  {
    key: "social-design-review-panel",
    title: "Review Comments",
    body: "Comments left on this design by your team or the client, pinned to specific spots on the canvas. Resolve a comment once it's addressed, or show resolved ones again to double-check.",
  },
  {
    key: "social-design-review-pins",
    title: "Comment Pins",
    body: "Each numbered pin marks where a reviewer left a comment on the design. Click a pin to open that comment.",
  },
  {
    key: "social-design-review-pin-capture",
    title: "Placing a Comment Pin",
    body: "Click anywhere on the design to drop a comment pin at that exact spot — press Escape to cancel without placing one.",
  },
  {
    key: "design-save-template",
    title: "Save as Template",
    body: "Saves this design as a reusable template so your team can start new designs from it later, instead of rebuilding it from scratch each time.",
  },
  {
    key: "design-font-picker",
    title: "Font Picker",
    body: "Choose a font for the selected text — the client's brand fonts are listed first, followed by the rest of the library.",
  },
  {
    key: "design-version-history",
    title: "Version History",
    body: "Every saved version of this design, so you can review or restore an earlier point — useful after a brand refresh or an edit you want to undo.",
  },
  {
    key: "design-editor-zoom",
    title: "Zoom",
    body: "Zoom the canvas in or out. This only changes your view — it doesn't resize the design itself.",
  },
  {
    key: "design-editor-animate",
    title: "Animate",
    body: "Add motion to this design — animate the whole page in or out, or give an individual element its own entrance, exit, or loop.",
  },
  {
    key: "design-animate-preview",
    title: "Preview",
    body: "Play back the design's animation as it will actually appear when exported.",
  },
  {
    key: "design-animate-page",
    title: "Animate Page",
    body: "Choose how the whole page animates in and out — the entrance and exit effect used when this page is exported as a video.",
  },
  {
    key: "design-animate-timing",
    title: "Page Timing",
    body: "Sets how long the current page is shown for when the design exports as a video or slideshow.",
  },
  {
    key: "design-animate-element",
    title: "Selected Element",
    body: "Give the selected element its own entrance, exit, or looping animation, independent of the page-level animation. Select one or more elements on the canvas first.",
  },
  {
    key: "design-export-video",
    title: "Export as Video",
    body: "Exports this design as a video or GIF. Animated designs render exactly as animated; a design with no animation yet can still export as a simple slideshow of its pages.",
  },
  {
    key: "design-export-slideshow",
    title: "Slideshow Options",
    body: "For a design with no animation, sets how many seconds each page shows for in the exported video.",
  },
  {
    key: "design-export-music",
    title: "Background Music",
    body: "Optionally add a music track to the exported video. Not available when exporting as a GIF.",
  },
  {
    key: "design-editor-ai-tools",
    title: "AI Tools",
    body: "AI-powered editing for the selected image or the whole design — background removal, inpainting, expanding, cropping, writing copy, and generating new images.",
  },
  {
    key: "design-editor-ai-remove-bg",
    title: "Background Remover",
    body: "Cuts the subject out of the selected image onto a transparent background.",
  },
  {
    key: "design-editor-ai-magic-eraser",
    title: "Magic Eraser & Edit",
    body: "Paint over part of the selected image to erase it, or describe what should replace the painted area instead.",
  },
  {
    key: "design-editor-ai-mask-erase",
    title: "Magic Eraser",
    body: "Paint over the part of the image you want removed, then run it — the painted area is erased and filled in automatically.",
  },
  {
    key: "design-editor-ai-mask-edit",
    title: "Magic Edit",
    body: "Paint over the part of the image you want to change, describe what it should become, then run it.",
  },
  {
    key: "design-editor-ai-magic-expand",
    title: "Magic Expand",
    body: "Extends the selected photo beyond its current edges to fit a new aspect ratio, filling in new content that matches the original.",
  },
  {
    key: "design-editor-ai-smart-crop",
    title: "Smart Crop",
    body: "Crops the selected image to a new shape while automatically keeping faces and the main subject in frame.",
  },
  {
    key: "design-editor-ai-upscale",
    title: "Upscale",
    body: "Makes the selected photo 2x or 4x bigger and sharper using our own on-server AI, so small or soft images print and post crisply. Free; the first run may take a minute while the engine warms up.",
  },
  {
    key: "design-editor-ai-grab-text",
    title: "Grab Text",
    body: "Reads the words in the selected photo and adds them as editable text layers placed over the same spot, so you can restyle or translate them. Undo with Ctrl+Z.",
  },
  {
    key: "design-editor-ai-magic-write",
    title: "Magic Write",
    body: "Generates caption or headline copy for this design using AI, in the client's brand voice if one is set.",
  },
  {
    key: "design-editor-ai-magic-design",
    title: "Magic Design",
    body: "Generates a full design layout from a short description, as a starting point you can then customize.",
  },
  {
    key: "design-editor-ai-generate-image",
    title: "Generate Image",
    body: "Creates a new image from a text description and inserts it onto the canvas.",
  },
  {
    key: "design-editor-ai-magic-resize",
    title: "Magic Resize",
    body: "Opens Magic Resize to automatically recreate this design at other canvas sizes.",
  },
  {
    key: "design-bulk-create",
    title: "Bulk Create from a Spreadsheet",
    body: "Merges a spreadsheet into this design — one new design per row. Upload a CSV, map its columns to text, images, a client brand, or a draft post caption, then check the rows before creating. Each row is created independently, and the results link to every new design.",
  },
  {
    key: "design-fan-out",
    title: "Apply to Clients",
    body: "Turns one master design into an on-brand draft for every client you select, in one action. Preview the brand-filled result per client first, optionally create a draft post for each, then create — one client failing never blocks the others.",
  },
  {
    key: "design-attach-to-post",
    title: "Attaching to a Post",
    body: "Shows progress while this design's pages render and attach as the post's images. If it fails, retry just this one or skip it without losing your place in a batch.",
  },
  {
    key: "design-editor-rail",
    title: "Editor Tools",
    body: "Switch between the editor's side panels — Elements, Text, Templates, Uploads, AI Tools, Animate, Layers, and Brand Kit.",
  },
  {
    key: "design-editor-save-status",
    title: "Save Status",
    body: "Shows whether your latest changes have saved. If it shows “Couldn't save” or “Offline”, fix your connection and retry — your edits stay in the editor until they do.",
  },
  {
    key: "design-editor-templates-panel",
    title: "Templates",
    body: "Browse and insert a template without leaving the editor — pick one that matches this design's canvas size to add it as a new page, or start fresh from a different size.",
  },
  {
    key: "design-editor-text-panel",
    title: "Text",
    body: "Insert a heading, body text, or a pre-styled text combo onto the canvas.",
  },
  {
    key: "design-studio-text-runs-toolbar",
    title: "Text Selection Formatting",
    body: "Appears when you select part of a text box's wording — bold, italic, underline, or change just the selected text's color, without affecting the rest of the text box.",
  },
];
